// Cozy accessibility menu (snippets/cozy-a11y.liquid): a round floating button fixed at the bottom-left
// corner, and a panel that opens upward from it. Each switch toggles a class on <html>; text size
// sets html.cozy-text-115/130/150. Choices persist per visitor in localStorage and the inline script in
// layout/theme.liquid re-applies them before first paint (same class names: keep both in step).
// "still" is the site's pause mechanism (WCAG 2.2.2 / IS 5568): components read window.cozyStill() and
// listen for the 'cozy:still' event (the marquee and hero pause buttons follow it both ways).
if (!customElements.get('cozy-a11y')) {
  const KEY = 'cozy-a11y';
  const MODES = {
    still: 'cozy-still',
    contrast: 'cozy-a11y-contrast',
    links: 'cozy-a11y-links',
    font: 'cozy-a11y-font',
    spacing: 'cozy-a11y-spacing',
    focus: 'cozy-a11y-focus',
    gray: 'cozy-a11y-gray',
    noimg: 'cozy-a11y-noimg',
  };
  const SIZES = ['115', '130', '150'];
  // Things docked at the bottom of the screen that the floating button must stay above (never cover):
  // the phone buy bar (product page), the phone checkout bar (cart page) and Shopify's cookie banner.
  const BOTTOM_BARS = '.cozy-sticky.is-visible, .cozy-cartpage__sticky.is-visible, #shopify-pc__banner';
  // Controls the phone tab must never cover (dodge()): every button (kit size chips, kit picks, CTAs, buy / add /
  // checkout, options, steppers), links (WhatsApp, policy, kit strip, card titles; not big image links taller than
  // 120px, which stay tappable around the tab), form fields and labels.
  const DODGE = 'button, [role="button"], .cozy-btn, .cozy-hero__btn, .cozy-card__add, [data-kb-qsize], [data-kb-cta], a[href], input:not([type="hidden"]), select, textarea, label, quantity-input';
  const read = () => {
    try {
      return JSON.parse(localStorage.getItem(KEY)) || {};
    } catch {
      return {};
    }
  };
  const write = (prefs) => {
    try {
      Object.keys(prefs).length ? localStorage.setItem(KEY, JSON.stringify(prefs)) : localStorage.removeItem(KEY);
    } catch {
      // Private mode / blocked storage: the switch still works for this page view.
    }
  };

  customElements.define(
    'cozy-a11y',
    class CozyA11y extends HTMLElement {
      connectedCallback() {
        this.button = this.querySelector('[data-a11y-open]');
        this.panel = this.querySelector('[data-a11y-panel]');
        this.status = this.querySelector('[data-a11y-status]');
        this.toggles = [...this.querySelectorAll('[data-a11y-mode]')];
        this.radios = [...this.querySelectorAll('[data-a11y-text]')];
        this.prefs = read();
        this.sync();

        // Once per element (the theme editor can move the header and reconnect it).
        if (!this.wired) {
          this.wired = true;
          this.button.addEventListener('click', () => (this.panel.hidden ? this.open() : this.close(true)));
          this.querySelector('[data-a11y-close]').addEventListener('click', () => this.close(true));
          this.querySelector('[data-a11y-reset]').addEventListener('click', () => this.reset());
          // The new value follows what the visitor sees on the switch.
          this.toggles.forEach((t) => t.addEventListener('click', () => this.set(t.dataset.a11yMode, t.getAttribute('aria-pressed') !== 'true')));
          this.radios.forEach((r) => r.addEventListener('change', () => r.checked && this.setSize(r.value)));
        }

        this.onKey = (e) => {
          if (e.key === 'Escape' && !this.panel.hidden) {
            e.stopPropagation();
            this.close(true);
          }
        };
        this.onPointer = (e) => {
          if (!this.panel.hidden && !this.contains(e.target)) this.close();
        };
        // Non-modal: tabbing out of the panel closes it (focus is already where the visitor went).
        this.onFocusOut = (e) => {
          if (!this.panel.hidden && e.relatedTarget && !this.contains(e.relatedTarget)) this.close();
        };
        // "Skip to the accessibility menu" (layout/theme.liquid): open it right away.
        this.onSkip = (e) => {
          if (!e.target.closest('[data-a11y-skip]')) return;
          e.preventDefault();
          this.open();
        };
        // Another tab changed the settings, or this page came back from the back/forward cache with old
        // classes: storage is the truth, the page and the switches follow it.
        this.onStorage = (e) => {
          if (e.key === KEY || e.key === null) this.refresh();
        };
        this.onPageShow = (e) => {
          if (e.persisted) this.refresh();
        };
        this.onResize = () => {
          this.queueLift();
          if (!this.panel.hidden) this.fit();
        };
        // The buy/checkout bars slide in on scroll and the cookie banner arrives late and leaves on a click:
        // re-measure on scroll, on DOM changes (rAF-throttled) and after each bar's slide transition.
        this.onScroll = () => this.queueLift();
        this.onTransition = (e) => e.target.matches?.('.cozy-sticky, .cozy-cartpage__sticky') && this.queueLift();
        this.observer = new MutationObserver(() => this.queueLift());
        this.observer.observe(document.body, { childList: true, subtree: true });
        this.barObserver = new MutationObserver(() => this.queueLift());
        document.querySelectorAll('.cozy-sticky, .cozy-cartpage__sticky').forEach((bar) => this.barObserver.observe(bar, { attributes: true, attributeFilter: ['class', 'hidden', 'style'] }));
        window.addEventListener('scroll', this.onScroll, { passive: true });
        document.addEventListener('transitionend', this.onTransition);
        document.addEventListener('click', this.onScroll);
        this.queueLift();
        document.addEventListener('keydown', this.onKey);
        document.addEventListener('pointerdown', this.onPointer);
        document.addEventListener('click', this.onSkip);
        window.addEventListener('storage', this.onStorage);
        window.addEventListener('pageshow', this.onPageShow);
        window.addEventListener('resize', this.onResize);
        this.addEventListener('focusout', this.onFocusOut);
        if (this.prefs.still) this.stopVideos();
      }

      disconnectedCallback() {
        document.removeEventListener('keydown', this.onKey);
        document.removeEventListener('pointerdown', this.onPointer);
        document.removeEventListener('click', this.onSkip);
        window.removeEventListener('storage', this.onStorage);
        window.removeEventListener('pageshow', this.onPageShow);
        window.removeEventListener('resize', this.onResize);
        window.removeEventListener('scroll', this.onScroll);
        document.removeEventListener('transitionend', this.onTransition);
        document.removeEventListener('click', this.onScroll);
        this.observer?.disconnect();
        this.barObserver?.disconnect();
        this.removeEventListener('focusout', this.onFocusOut);
      }

      queueLift() {
        if (this.liftFrame) return;
        this.liftFrame = requestAnimationFrame(() => {
          this.liftFrame = 0;
          this.lift();
          // A bar mid-slide: measure once more when it has settled.
          clearTimeout(this.liftTimer);
          this.liftTimer = setTimeout(() => this.lift(), 400);
        });
      }

      // --cozy-a11y-lift = how far the bottom-left corner is covered by a docked bar or banner (only ones
      // that reach the button's column and sit near the bottom). The button and panel rise by that much.
      lift() {
        const edge = 16;
        const reach = edge + this.button.offsetWidth + 8;
        let lift = 0;
        document.querySelectorAll(BOTTOM_BARS).forEach((el) => {
          const cs = getComputedStyle(el);
          if (cs.display === 'none' || cs.visibility === 'hidden' || el.hidden) return;
          const r = el.getBoundingClientRect();
          if (!r.height || r.left > reach || r.right < edge) return;
          if (r.bottom < innerHeight - 120 || r.top >= innerHeight) return;
          // A bar that is still sliding in reports its moving top; use its full height when it is shown.
          const top = el.matches('.is-visible') ? innerHeight - el.offsetHeight : r.top;
          lift = Math.max(lift, Math.ceil(innerHeight - top));
        });
        lift += this.dodge(lift);
        const value = `${lift}px`;
        if (this.liftValue !== value) {
          this.liftValue = value;
          // Set on this element (the button and panel are its children), not on <html>: a custom property on
          // <html> made every element on the page recalculate its style each time the bar moved.
          this.style.setProperty('--cozy-a11y-lift', value);
          if (!this.panel.hidden) this.fit();
        }
      }

      // 2026-10-23 (phones only): the button is a small tab on the left edge; when the page scrolls a size chip,
      // buy/add/checkout button or quantity control under it, it hops up just above that control (at most to
      // the top of the screen), so it never covers one and stays visible and tappable. Frozen while the
      // panel is open. Returns the extra lift in px on top of the docked-bar lift.
      dodge(barLift) {
        if (!matchMedia('(max-width: 749px)').matches || !this.panel.hidden) return this.panel.hidden ? 0 : this.dodgeValue || 0;
        const b = this.button;
        const cs = getComputedStyle(b);
        const h = b.offsetHeight;
        const left = parseFloat(cs.left) || 0;
        const right = left + b.offsetWidth;
        const baseBottom = innerHeight - (parseFloat(cs.bottom) || 0) - barLift;
        const gap = 6;
        const rects = [];
        document.querySelectorAll(DODGE).forEach((el) => {
          // Docked bars and the cookie banner are handled by the bar lift above.
          if (el.closest('.cozy-a11y, .cozy-sticky, .cozy-cartpage__sticky, #shopify-pc__banner')) return;
          const r = el.getBoundingClientRect();
          if (r.width < 1 || r.height < 1 || r.right < left || r.left > right + gap) return;
          if (el.tagName === 'A' && r.height > 120) return;
          if (r.bottom < 0 || r.top > innerHeight) return;
          const s = getComputedStyle(el);
          if (s.visibility === 'hidden' || s.display === 'none') return;
          rects.push(r);
        });
        const hit = (bottom) => rects.find((r) => r.top < bottom + gap && r.bottom > bottom - h - gap);
        // Up first: hop above whatever it would cover, repeatedly.
        let bottom = baseBottom;
        let r;
        let n = 0;
        while ((r = hit(bottom)) && n++ < 12) bottom = r.top - gap;
        // Never above the top of the screen (a stack of controls taller than the screen): stay visible at the top.
        bottom = Math.max(8 + h, bottom);
        this.dodgeValue = Math.max(0, Math.round(baseBottom - bottom));
        return this.dodgeValue;
      }

      refresh() {
        const wasStill = Boolean(this.prefs.still);
        this.prefs = read();
        this.sync();
        if (Boolean(this.prefs.still) !== wasStill) this.announceStill(Boolean(this.prefs.still));
      }

      open() {
        this.panel.hidden = false;
        this.button.setAttribute('aria-expanded', 'true');
        document.documentElement.classList.add('cozy-a11y-open');
        this.lift();
        this.fit();
        // Into the dialog: screen readers hear its name ("נגישות"), the next Tab reaches text size.
        this.panel.focus();
      }

      close(refocus) {
        this.panel.hidden = true;
        this.button.setAttribute('aria-expanded', 'false');
        document.documentElement.classList.remove('cozy-a11y-open');
        if (refocus) this.button.focus();
      }

      // Keep the panel on screen: it opens upward from the floating button (CSS: fixed, bottom-left, above
      // the button), so it may use the space between the top of the window and the button; its own content
      // scrolls if it is taller (big text sizes, short phones in landscape).
      fit() {
        const top = this.button.getBoundingClientRect().top;
        this.panel.style.maxHeight = `${Math.max(160, Math.floor(top - 10 - 12))}px`;
      }

      label(el) {
        return el.querySelector('span')?.textContent.trim() || '';
      }

      set(mode, on) {
        this.prefs = read(); // another tab may have changed other settings meanwhile
        if (on) this.prefs[mode] = 1;
        else delete this.prefs[mode];
        write(this.prefs);
        this.sync();
        const toggle = this.toggles.find((t) => t.dataset.a11yMode === mode);
        this.announce(`${this.label(toggle)}: ${on ? this.dataset.on : this.dataset.off}`);
        if (mode === 'still') this.announceStill(on);
      }

      setSize(size) {
        this.prefs = read();
        if (SIZES.includes(size)) this.prefs.text = size;
        else delete this.prefs.text;
        write(this.prefs);
        this.sync();
        this.announce(this.dataset.size.replace('[size]', size));
      }

      reset() {
        const wasStill = Boolean(read().still);
        this.prefs = {};
        write(this.prefs);
        this.sync();
        this.announce(this.dataset.resetDone);
        if (wasStill) this.announceStill(false);
      }

      sync() {
        const root = document.documentElement;
        for (const [mode, cls] of Object.entries(MODES)) root.classList.toggle(cls, Boolean(this.prefs[mode]));
        SIZES.forEach((s) => root.classList.toggle(`cozy-text-${s}`, this.prefs.text === s));
        this.toggles.forEach((t) => t.setAttribute('aria-pressed', String(Boolean(this.prefs[t.dataset.a11yMode]))));
        const size = SIZES.includes(this.prefs.text) ? this.prefs.text : '100';
        this.radios.forEach((r) => (r.checked = r.value === size));
      }

      announce(message) {
        if (!this.status || !message) return;
        this.status.textContent = '';
        setTimeout(() => (this.status.textContent = message), 50);
      }

      announceStill(still) {
        if (still) this.stopVideos();
        document.dispatchEvent(new CustomEvent('cozy:still', { detail: { still } }));
      }

      // Covers any playing video, including ones without their own listener.
      stopVideos() {
        document.querySelectorAll('video').forEach((v) => v.paused || v.pause());
      }
    }
  );
}
